import React, { useState } from "react";

export const SummaryStats = ({ summaryData, loading, dateRangeType }) => {
  const [showMostPopover, setShowMostPopover] = useState(false);
  const [showLeastPopover, setShowLeastPopover] = useState(false);

  if (loading) {
    return (
      <div className="col-12">
        <div className="card">
          <div className="card-body">
            <div className="text-center">Loading summary stats...</div>
          </div>
        </div>
      </div>
    );
  }

  const {
    uniqueUsers = 0,
    uniqueUsersPercentage = 0,
    totalEmployee = 0,
    averageUtilization = 0,
    averageUtilizationPercentage = 0,
    mostUtilizedHours = [],
    leastUtilizedHours = [],
  } = summaryData || {};

  const formatHour = (hour) => {
    if (hour === null || hour === undefined) return 'N/A';

    // For weekly/monthly/custom, the API returns formatted strings like "Jun 8 (Mon) 4:00 AM"
    // For daily, it returns just the hour number
    if (typeof hour === 'string') {
      return hour;
    }

    const h = typeof hour === 'number' ? hour : parseInt(hour);
    if (isNaN(h)) return 'N/A';

    // Format for daily view (numeric hour)
    return `${h % 12 || 12}:00 ${h >= 12 ? "PM" : "AM"}`;
  };

  return (
    <>
      {/* Unique Employees */}
      <div className="col-sm-6 col-lg-3">
        <div className="card">
          <div className="card-body">
            <div className="subheader">Unique Employees</div>
            <div className="d-flex align-items-center gap-1 mb-2">
              <div className="h1 mb-0">{uniqueUsersPercentage.toFixed(1)}%</div>
              <div className="text-muted">({uniqueUsers})</div>
            </div>
            <div className="progress progress-sm mb-2">
              <div
                className="progress-bar bg-primary"
                style={{ width: `${uniqueUsersPercentage}%` }}
                role="progressbar"
              />
            </div>
            <div className="text-muted small">Expected Employee: {totalEmployee}</div>
          </div>
        </div>
      </div>

      {/* Average Utilization */}
      <div className="col-sm-6 col-lg-3">
        <div className="card">
          <div className="card-body">
            <div className="subheader">Average Utilization</div>
            <div className="d-flex align-items-center gap-1 mb-2">
              <div className="h1 mb-0">{averageUtilizationPercentage.toFixed(1)}%</div>
              <div className="text-muted">({averageUtilization.toFixed(0)})</div>
            </div>
            <div className="progress progress-sm mb-2">
              <div
                className="progress-bar bg-primary"
                style={{ width: `${averageUtilizationPercentage}%` }}
                role="progressbar"
              />
            </div>
            <div className="text-muted small">Across selected period</div>
          </div>
        </div>
      </div>

      {/* Most Utilized Hour */}
      <div className="col-sm-6 col-lg-3">
        <div className="card">
          <div className="card-body">
            <div className="d-flex align-items-center justify-content-between mb-2">
              <div className="subheader">Most Utilized Hour</div>
              {mostUtilizedHours.length > 3 && (
                <div className="dropdown position-relative">
                  <button
                    className="btn btn-sm btn-ghost-primary p-1"
                    type="button"
                    onClick={() => setShowMostPopover(!showMostPopover)}
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="icon icon-tabler icon-tabler-plus" width="16" height="16" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round">
                      <path stroke="none" d="M0 0h24v24H0z" fill="none"/>
                      <line x1="12" y1="5" x2="12" y2="19" />
                      <line x1="5" y1="12" x2="19" y2="12" />
                    </svg>
                  </button>
                  {showMostPopover && (
                    <>
                      <div
                        className="dropdown-menu dropdown-menu-end show"
                        style={{
                          maxHeight: "300px",
                          overflowY: "auto",
                          minWidth: "280px",
                          maxWidth: "320px",
                          position: "absolute",
                          top: "100%",
                          right: 0,
                          zIndex: 1050
                        }}
                      >
                        <div className="dropdown-header fw-bold">
                          All ({mostUtilizedHours.length})
                        </div>
                        {mostUtilizedHours.map((item, index) => (
                          <div key={index} className="dropdown-item" style={{ whiteSpace: "normal", wordBreak: "break-word" }}>
                            <div className="d-flex justify-content-between w-100 gap-2">
                              <span className="fw-bold flex-shrink-0">{formatHour(item.hour)}</span>
                              <span className="text-muted text-end">{item.percentage.toFixed(1)}% ({item.count})</span>
                            </div>
                            {index !== mostUtilizedHours.length - 1 && <hr className="dropdown-divider" />}
                          </div>
                        ))}
                      </div>
                      <div className="modal-backdrop fade show" onClick={() => setShowMostPopover(false)} style={{ zIndex: 1040 }} />
                    </>
                  )}
                </div>
              )}
            </div>
            <div className="d-flex flex-wrap gap-1 mb-2">
              {mostUtilizedHours.slice(0, 3).map((item, index) => (
                <div
                  key={index}
                  className={`fw-bold ${mostUtilizedHours.length === 1 ? 'h2' : 'small'}`}
                >
                  {formatHour(item.hour)}
                </div>
              ))}
            </div>
            <div className="progress progress-sm mb-2">
              <div
                className="progress-bar bg-primary"
                style={{ width: `${mostUtilizedHours[0]?.percentage || 0}%` }}
                role="progressbar"
              />
            </div>
            {mostUtilizedHours.length > 0 && (
              <div className="text-muted small">
                {mostUtilizedHours[0].percentage.toFixed(1)}% utilization ({mostUtilizedHours[0].count})
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Least Utilized Hour */}
      <div className="col-sm-6 col-lg-3">
        <div className="card">
          <div className="card-body">
            <div className="d-flex align-items-center justify-content-between mb-2">
              <div className="subheader">Least Utilized Hour</div>
              {leastUtilizedHours.length > 3 && (
                <div className="dropdown position-relative">
                  <button
                    className="btn btn-sm btn-ghost-primary p-1"
                    type="button"
                    onClick={() => setShowLeastPopover(!showLeastPopover)}
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="icon icon-tabler icon-tabler-plus" width="16" height="16" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round">
                      <path stroke="none" d="M0 0h24v24H0z" fill="none"/>
                      <line x1="12" y1="5" x2="12" y2="19" />
                      <line x1="5" y1="12" x2="19" y2="12" />
                    </svg>
                  </button>
                  {showLeastPopover && (
                    <>
                      <div
                        className="dropdown-menu dropdown-menu-end show"
                        style={{
                          maxHeight: "300px",
                          overflowY: "auto",
                          minWidth: "280px",
                          maxWidth: "320px",
                          position: "absolute",
                          top: "100%",
                          right: 0,
                          zIndex: 1050
                        }}
                      >
                        <div className="dropdown-header fw-bold">
                          All ({leastUtilizedHours.length})
                        </div>
                        {leastUtilizedHours.map((item, index) => (
                          <div key={index} className="dropdown-item" style={{ whiteSpace: "normal", wordBreak: "break-word" }}>
                            <div className="d-flex justify-content-between w-100 gap-2">
                              <span className="fw-bold flex-shrink-0">{formatHour(item.hour)}</span>
                              <span className="text-muted text-end">{item.percentage.toFixed(1)}% ({item.count})</span>
                            </div>
                            {index !== leastUtilizedHours.length - 1 && <hr className="dropdown-divider" />}
                          </div>
                        ))}
                      </div>
                      <div className="modal-backdrop fade show" onClick={() => setShowLeastPopover(false)} style={{ zIndex: 1040 }} />
                    </>
                  )}
                </div>
              )}
            </div>
            <div className="d-flex flex-wrap gap-1 mb-2">
              {leastUtilizedHours.slice(0, 3).map((item, index) => (
                <div
                  key={index}
                  className={`fw-bold ${leastUtilizedHours.length === 1 ? 'h2' : 'small'}`}
                >
                  {formatHour(item.hour)}
                </div>
              ))}
            </div>
            <div className="progress progress-sm mb-2">
              <div
                className="progress-bar bg-primary"
                style={{ width: `${leastUtilizedHours[0]?.percentage || 0}%` }}
                role="progressbar"
              />
            </div>
            {leastUtilizedHours.length > 0 && (
              <div className="text-muted small">
                {leastUtilizedHours[0].percentage.toFixed(1)}% utilization ({leastUtilizedHours[0].count})
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};
